import { useState, useEffect, useRef } from 'react';
import { Entity } from '../../types/apiTypes';
import { Path, Storefront } from '@phosphor-icons/react';
import { AccentButton } from '../../shared/Buttons';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useAuth } from '../../utils/AuthContext';
import useApi from '../../utils/AxiosWrapper';
import { HttpStatusCode } from 'axios';
import { getEtid } from '../../shared/etid';
import { clearPartnerCode, getPartnerCode } from '../../shared/partnerCode';
import { clearLandingCookie, getLandingTouches } from '../../shared/landing';
import { ClipLoader } from 'react-spinners';
import { trackApplicationStarted } from '../../utils/ga4';

import {
  safeGetItem,
  safeRemoveItem,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSetItem,
} from '../../shared/safeStorage';

// Helper to read cookies (used for tier selection from landing page)
const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};

function RoleSelect() {
  const [selectedRole, setSelectedRole] = useState<Entity | null>(null);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isAutoSubmitting, setIsAutoSubmitting] = useState(false);
  const { screenView } = useScreenView();
  const { user } = useAuth();
  const { callProtectedApi } = useApi();
  const hasAutoSubmitted = useRef(false);
  const roles: { entity: Entity; icon: any; desc: string; label: string }[] = [
    {
      label: 'Carrier',
      entity: 'customer' as Entity,
      icon: '/truck.svg',
      desc: 'I operate trucks to provide shipping and transportation services.',
    },
    {
      label: 'Vendor or Merchant',
      entity: 'vendor' as Entity,
      icon: <Storefront size={24} />,
      desc: 'I provide goods or services related to the trucking industry.',
    },
    {
      label: 'Logistics Broker',
      entity: 'partner' as Entity,
      icon: <Path size={24} />,
      desc: 'I arrange and manage transportation and shipping services between carriers and shippers.',
    },
  ];

  // Function to handle role submission
  const handleSubmitRole = async (role: Entity) => {
    if (!role || !user || isCreatingUser) {
      return;
    }

    setIsCreatingUser(true);
    try {
      let roleUsed = role.toString();
      if (role === Entity.Driver) {
        roleUsed = 'driverInvites';
      } else if (role === Entity.PartnerAgent) {
        roleUsed = 'partnerAgentInvites';
      } else {
        roleUsed = role.toLowerCase() + 's';
      }

      // Get tachref from localStorage if it exists (for URL navigation only)
      const tachref = safeGetItem('tachref');

      const endpoint = `/${roleUsed}`;

      // Read tier selection from cookie (set by landing page)
      // Valid values: 'starter', 'core', 'pro'
      const selectedTier = getCookie('tach_selected_tier');

      // Read etid through the shared reader (cookie + localStorage), not the
      // localStorage mirror alone — this is the last hop before the ETID is
      // sent to the backend, so a single-channel read here silently loses
      // attribution for anyone whose localStorage is unavailable.
      const etid = getEtid();

      // Co-branded application link: the partner code stored by login.tsx
      // (or restored by index.tsx after the Auth0 round-trip).
      const partnerCode = getPartnerCode();

      // Where the carrier arrived on tachusa.com (URL + referrer), recorded by
      // the marketing site in a `.tachusa.com` cookie.
      const landingTouches = getLandingTouches();

      const response = await callProtectedApi(
        endpoint,
        role !== Entity.Driver && role !== Entity.PartnerAgent ? 'post' : 'get',
        {
          email: user?.email,
          sub_id: user?.sub,
          email_verified: user?.email_verified,
          phone_number: user?.phone_number,
          // Include selected tier if available (only for customers)
          ...(role === Entity.Customer &&
            selectedTier && { selected_tier: selectedTier }),
          // Include email template id if available (only for customers)
          ...(role === Entity.Customer && etid && { email_template_id: etid }),
          // Include the partner code if available (only for customers)
          ...(role === Entity.Customer &&
            partnerCode && { third_party_logistics_slug: partnerCode }),
          // Include the landing touches if available (only for customers)
          ...(role === Entity.Customer &&
            landingTouches && { landing_touches: landingTouches }),
        },
        `Error occurred while creating ${role} account.`
      );

      if (
        response?.status === HttpStatusCode.Ok ||
        response?.status === HttpStatusCode.Created
      ) {
        // A carrier has created a login and is about to land on screen one — the moment the
        // admin portal counts as "application started". No application row exists yet; it is
        // not written until screen one is submitted.
        //
        // Carriers only. Vendors and brokers come through this same handler, and counting them
        // is precisely what made signup_funnel_completed unusable: bodies in the numerator that
        // can never appear in a carrier-only denominator.
        //
        // Gated on 201 rather than 2xx because the endpoint returns 200 with "Customer already
        // exists" for a repeat call, and a re-reported start would inflate the top of the funnel.
        if (
          role === Entity.Customer &&
          response?.status === HttpStatusCode.Created
        ) {
          trackApplicationStarted();
        }

        // Clear the tachRole from storage after successful submission
        safeRemoveItem('tachRole');
        safeSessionRemoveItem('tachRole');
        // The customer record now carries the partner; a stale code must not
        // ride along on a later account created in this browser.
        clearPartnerCode();
        // Same for the landing cookie: it describes this carrier's arrival.
        clearLandingCookie();

        // User created successfully - always go to signup for new users
        // New users have pending status and need to complete signup
        // Append tachref to URL if it exists
        const signupUrl = tachref
          ? `/signup?tachref=${encodeURIComponent(tachref)}`
          : '/signup';
        window.location.href = signupUrl;
      }
    } catch (error: any) {
      console.error('Error creating user:', error?.message);
      // Still navigate to signup, UserContext will handle retry
      // Preserve tachref in URL if it exists
      const tachref = safeGetItem('tachref');
      const signupUrl = tachref
        ? `/signup?tachref=${encodeURIComponent(tachref)}`
        : '/signup';
      window.location.href = signupUrl;
    }
  };

  // Check for pre-filled tachRole and auto-submit
  useEffect(() => {
    // Check both localStorage and sessionStorage for tachRole
    const storedRole =
      safeGetItem('tachRole') || safeSessionGetItem('tachRole');

    if (storedRole && !hasAutoSubmitted.current) {
      // Map the stored role string to Entity
      const roleEntity = storedRole as Entity;

      // Only proceed if it's a valid role
      if (
        roleEntity === 'customer' ||
        roleEntity === 'vendor' ||
        roleEntity === 'partner'
      ) {
        setSelectedRole(roleEntity);
        safeSetItem('tachUserRole', roleEntity);
        setIsAutoSubmitting(true);

        // Auto-submit if user is authenticated
        if (user && !isCreatingUser) {
          hasAutoSubmitted.current = true;
          handleSubmitRole(roleEntity);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Depend on user to ensure we auto-submit once auth is ready

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div
            className={`flex min-h-[100vh] w-full max-w-[1500px] flex-col items-center justify-center overflow-auto py-[48px] align-middle`}
          >
            <div
              className={`xl:overflow-hidden flex w-full flex-row items-center justify-between overflow-y-auto align-middle`}
            >
              <div className="flex w-[50%] flex-col items-center justify-center space-y-[32px] px-[100px]">
                <div className="flex w-full flex-col items-start justify-start gap-y-2">
                  <h3 className="text-start text-[32px] font-[600] text-primary">
                    What type of company are you?
                  </h3>
                  <p className="text-[16px] font-[400] text-graysecondary">
                    {isAutoSubmitting
                      ? 'Auto-selecting your role based on your selection...'
                      : 'Select the role that best describes your business.'}
                  </p>
                </div>
                <div
                  className={`flex h-full w-full flex-col items-center justify-center space-y-4`}
                >
                  {roles.map((role, idx) => {
                    return (
                      <div
                        className={`flex h-[152px] max-h-[152px] w-full flex-col items-start gap-y-3 rounded-[4px] border-[1px] bg-white p-6 hover:cursor-pointer hover:border-accent ${selectedRole === role.entity ? 'border-[1px] border-accent bg-bgyellow' : 'border-bordergray'}`}
                        onClick={() => {
                          setSelectedRole(role.entity as Entity);
                          safeSetItem('tachUserRole', role.entity);
                        }}
                      >
                        <div className="flex flex-row items-center justify-start gap-x-3">
                          <div className="flex h-[44px] w-[44px] flex-row items-center justify-center rounded-full border-[1px] border-accent bg-white">
                            {role?.entity === 'customer' ? (
                              <img
                                alt={`${role?.entity}-icon`}
                                src={role.icon}
                                className="h-[24px] w-[24px]"
                              />
                            ) : (
                              role?.icon
                            )}
                          </div>
                          <h2 className="text-[22px] font-[700] text-primary">
                            {role?.label}
                          </h2>
                        </div>
                        <p className="text-[16px] font-[400] text-primary">
                          {role?.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <AccentButton
                  disabled={selectedRole === null || isCreatingUser}
                  addClass="w-[250px] h-[48px] flex flex-row items-center justify-center rounded-full disabled:bg-opacity-60 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (selectedRole) {
                      handleSubmitRole(selectedRole);
                    }
                  }}
                >
                  {isCreatingUser ? (
                    <div className="flex flex-row items-center justify-center gap-x-2">
                      <ClipLoader
                        color="#1D213B"
                        loading={isCreatingUser}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                      <span>Starting application...</span>
                    </div>
                  ) : (
                    'Continue'
                  )}
                </AccentButton>
              </div>
              <div className="relative flex w-[50%] flex-col items-center justify-center space-y-[32px]">
                <img
                  src="/EndlessHighway.png"
                  alt="Endless Highway"
                  className="rounded-[8px] lg:h-[700px]"
                />
                <img
                  src="/TachLogo.svg"
                  alt="TACH Logo"
                  className="absolute left-[50%] top-4 z-40 h-[24px] translate-x-[-50%]"
                />
              </div>
            </div>
          </div>
        );
      case 'tablet':
        return (
          <div
            className={`flex h-full max-h-[100vh] w-[80%] flex-col items-start justify-between overflow-auto align-middle`}
          >
            <div
              className={`flex w-full flex-col items-center justify-between gap-y-[32px] overflow-y-auto py-[48px] align-middle`}
            >
              <div className="relative flex w-full flex-col items-center justify-center space-y-[32px]">
                <img
                  src="/EndlessHighway.png"
                  alt="Endless Highway"
                  className="h-[141px] max-h-[141px] w-full rounded-[8px]"
                />
                <img
                  src="/TachLogo.svg"
                  alt="TACH Logo"
                  className="absolute left-[50%] top-[5%] z-40 h-[24px] translate-x-[-50%] translate-y-[-50%]"
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center space-y-[32px] px-[24px]">
                <div className="flex w-full flex-col items-start justify-start gap-y-2">
                  <h3 className="text-start text-[32px] font-[600] text-primary">
                    What type of company are you?
                  </h3>
                  <p className="text-[16px] font-[400] text-graysecondary">
                    {isAutoSubmitting
                      ? 'Auto-selecting your role based on your selection...'
                      : 'Select the role that best describes your business.'}
                  </p>
                </div>
                <div
                  className={`flex h-full w-full flex-col items-center justify-center space-y-4`}
                >
                  {roles.map((role, idx) => {
                    return (
                      <div
                        className={`flex h-[152px] max-h-[152px] w-full flex-col items-start gap-y-3 rounded-[4px] border-[1px] bg-white p-6 hover:cursor-pointer hover:border-accent ${selectedRole === role.entity ? 'border-[1px] border-accent bg-bgyellow' : 'border-bordergray'}`}
                        onClick={() => {
                          setSelectedRole(role.entity as Entity);
                          safeSetItem('tachUserRole', role.entity);
                        }}
                      >
                        <div className="flex flex-row items-center justify-start gap-x-3">
                          <div className="flex h-[44px] w-[44px] flex-row items-center justify-center rounded-full border-[1px] border-accent bg-white">
                            {role?.entity === 'customer' ? (
                              <img
                                alt={`${role?.entity}-icon`}
                                src={role.icon}
                                className="h-[24px] w-[24px]"
                              />
                            ) : (
                              role?.icon
                            )}
                          </div>
                          <h2 className="text-[22px] font-[700] text-primary">
                            {role?.label}
                          </h2>
                        </div>
                        <p className="text-[16px] font-[400] text-primary">
                          {role?.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <AccentButton
                  disabled={selectedRole === null || isCreatingUser}
                  addClass="w-[250px] h-[48px] flex flex-row items-center justify-center rounded-full disabled:bg-opacity-60 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (selectedRole) {
                      handleSubmitRole(selectedRole);
                    }
                  }}
                >
                  {isCreatingUser ? (
                    <div className="flex flex-row items-center justify-center gap-x-2">
                      <ClipLoader
                        color="#1D213B"
                        loading={isCreatingUser}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                      <span>Starting application...</span>
                    </div>
                  ) : (
                    'Continue'
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div
            className={`flex h-full max-h-[100vh] w-[90%] flex-col items-start justify-between overflow-auto align-middle`}
          >
            <div
              className={`flex w-full flex-col items-center justify-between gap-y-[32px] overflow-y-auto py-[48px] align-middle`}
            >
              <div className="relative flex w-full flex-col items-center justify-center space-y-[32px]">
                <img
                  src="/EndlessHighway.png"
                  alt="Endless Highway"
                  className="h-[141px] max-h-[141px] w-full rounded-[8px]"
                />
                <img
                  src="/TachLogo.svg"
                  alt="TACH Logo"
                  className="absolute left-[50%] top-[5%] z-40 h-[24px] translate-x-[-50%] translate-y-[-50%]"
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center space-y-[32px] px-[24px]">
                <div className="flex w-full flex-col items-start justify-start gap-y-2">
                  <h3 className="text-start text-[32px] font-[600] text-primary">
                    What type of company are you?
                  </h3>
                  <p className="text-[16px] font-[400] text-graysecondary">
                    {isAutoSubmitting
                      ? 'Auto-selecting your role based on your selection...'
                      : 'Select the role that best describes your business.'}
                  </p>
                </div>
                <div
                  className={`flex h-full w-full flex-col items-center justify-center space-y-4`}
                >
                  {roles.map((role, idx) => {
                    return (
                      <div
                        className={`flex h-[224px] max-h-[224px] w-full flex-col items-start gap-y-3 rounded-[4px] border-[1px] bg-white p-6 hover:cursor-pointer hover:border-accent ${selectedRole === role.entity ? 'border-[1px] border-accent bg-bgyellow' : 'border-bordergray'}`}
                        onClick={() => {
                          setSelectedRole(role.entity as Entity);
                          safeSetItem('tachUserRole', role.entity);
                        }}
                      >
                        <div className="flex flex-row items-center justify-start gap-x-3">
                          <div className="flex h-[44px] w-[44px] flex-row items-center justify-center rounded-full border-[1px] border-accent bg-white">
                            {role?.entity === 'customer' ? (
                              <img
                                alt={`${role?.entity}-icon`}
                                src={role.icon}
                                className="h-[24px] w-[24px]"
                              />
                            ) : (
                              role?.icon
                            )}
                          </div>
                          <h2 className="text-[22px] font-[700] text-primary">
                            {role?.label}
                          </h2>
                        </div>
                        <p className="text-[16px] font-[400] text-primary">
                          {role?.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <AccentButton
                  disabled={selectedRole === null || isCreatingUser}
                  addClass="w-[250px] h-[48px] flex flex-row items-center justify-center rounded-full disabled:bg-opacity-60 disabled:cursor-not-allowed"
                  onClick={() => {
                    if (selectedRole) {
                      handleSubmitRole(selectedRole);
                    }
                  }}
                >
                  {isCreatingUser ? (
                    <div className="flex flex-row items-center justify-center gap-x-2">
                      <ClipLoader
                        color="#1D213B"
                        loading={isCreatingUser}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                      <span>Starting application...</span>
                    </div>
                  ) : (
                    'Continue'
                  )}
                </AccentButton>
              </div>
            </div>
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default RoleSelect;
